import React, { useState, useEffect } from 'react';
import { slpService } from '../../lib/services/slp';

const StudentRecords = ({ students, selectedStudent, onStudentSelect }) => {
  const [studentRecords, setStudentRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (selectedStudent) {
      loadStudentRecords(selectedStudent.id);
    } else {
      setStudentRecords([]);
    }
  }, [selectedStudent]);

  const loadStudentRecords = async (studentId) => {
    try {
      setLoading(true);
      setError(null);
      
      const records = await slpService.getStudentRecords(studentId);
      setStudentRecords(records);
    } catch (err) {
      console.error('Error loading student records:', err);
      setError('載入學生記錄時發生錯誤');
    } finally {
      setLoading(false);
    }
  };

  const getAchievementColor = (level) => {
    const colors = {
      '優秀': 'bg-green-100 text-green-800',
      '良好': 'bg-blue-100 text-blue-800',
      '合格': 'bg-yellow-100 text-yellow-800',
      '參與': 'bg-gray-100 text-gray-800'
    };
    return colors[level] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    return `${year}年${month}月${day}日`;
  };

  const getCategoryIcon = (category) => {
    const icons = {
      '學術': '📚',
      '體育': '🏃',
      '藝術': '🎨',
      '社會服務': '🤝',
      '領袖訓練': '👑',
      '考試': '📝',
      '假期': '🎊',
      '典禮': '🏛️',
      '競賽': '🏆'
    };
    return icons[category] || '📋';
  };

  const getCategoryColor = (category) => {
    const colors = {
      '學術': 'blue',
      '體育': 'green',
      '藝術': 'purple',
      '社會服務': 'yellow',
      '領袖訓練': 'red',
      '考試': 'darkred',
      '假期': 'darkgreen',
      '典禮': 'darkblue',
      '競賽': 'orange'
    };
    return colors[category] || 'gray';
  };

  const getStudentStats = () => {
    if (!selectedStudent || studentRecords.length === 0) {
      return {
        totalActivities: 0,
        categoryStats: {},
        achievementStats: {}
      };
    }

    const categoryStats = {};
    const achievementStats = {};

    studentRecords.forEach(record => {
      const category = record.saas_slp_activities?.activity_category;
      const achievement = record.achievement_level;

      if (category) {
        categoryStats[category] = (categoryStats[category] || 0) + 1;
      }

      if (achievement) {
        achievementStats[achievement] = (achievementStats[achievement] || 0) + 1;
      }
    });

    return {
      totalActivities: studentRecords.length,
      categoryStats,
      achievementStats
    };
  };

  const stats = getStudentStats();

  return (
    <div className="student-records">
      <div className="student-records-header">
        <h2>學生記錄</h2>
        <p>查看和管理學生的學習歷程記錄</p>
      </div>

      <div className="student-selection">
        <h3>選擇學生</h3>
        <div className="students-grid">
          {students.map(student => (
            <div 
              key={student.id} 
              className={`student-card ${selectedStudent?.id === student.id ? 'selected' : ''}`}
              onClick={() => onStudentSelect(student)}
            >
              <div className="student-avatar">
                <span className="avatar-text">
                  {student.student_name.charAt(0)}
                </span>
              </div>
              <div className="student-info">
                <h4 className="student-name">{student.student_name}</h4>
                <p className="student-id">學號: {student.student_id}</p>
                <p className="student-class">班別: {student.class_id}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {selectedStudent && (
        <div className="student-details">
          <div className="student-profile">
            <div className="profile-header">
              <div className="profile-avatar">
                <span className="avatar-text">
                  {selectedStudent.student_name.charAt(0)}
                </span>
              </div>
              <div className="profile-info">
                <h3>{selectedStudent.student_name}</h3>
                <p>學號: {selectedStudent.student_id}</p>
                <p>班別: {selectedStudent.class_id}</p>
              </div>
            </div>

            <div className="profile-stats">
              <div className="stat-card">
                <div className="stat-number">{stats.totalActivities}</div>
                <div className="stat-label">總參與活動數</div>
              </div>
              
              <div className="stat-card">
                <div className="stat-number">{Object.keys(stats.categoryStats).length}</div>
                <div className="stat-label">參與類別數</div>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>載入學生記錄中...</p>
            </div>
          ) : error ? (
            <div className="error-container">
              <p>❌ {error}</p>
              <button onClick={() => loadStudentRecords(selectedStudent.id)} className="retry-btn">
                重新載入
              </button>
            </div>
          ) : (
            <>
              {/* 統計分析 */}
              <div className="records-analysis">
                <div className="analysis-section">
                  <h4>活動類別分布</h4>
                  <div className="category-stats">
                    {Object.entries(stats.categoryStats).map(([category, count]) => (
                      <div key={category} className="category-stat">
                        <span className="category-icon">{getCategoryIcon(category)}</span>
                        <span className="category-name">{category}</span>
                        <span className="category-count">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="analysis-section">
                  <h4>成就等級分布</h4>
                  <div className="achievement-stats">
                    {Object.entries(stats.achievementStats).map(([level, count]) => (
                      <div key={level} className="achievement-stat">
                        <span className={`achievement-badge ${getAchievementColor(level)}`}>
                          {level}
                        </span>
                        <span className="achievement-count">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* 活動記錄列表 */}
              <div className="records-list">
                <h4>活動參與記錄</h4>
                {studentRecords.length === 0 ? (
                  <div className="empty-records">
                    <div className="empty-icon">📋</div>
                    <h5>暫無參與記錄</h5>
                    <p>該學生尚未參與任何活動</p>
                  </div>
                ) : (
                  <div className="records-grid">
                    {studentRecords.map(record => (
                      <div key={record.id} className="record-card">
                        <div className="record-header">
                          <div className="activity-category">
                            <span className={`category-icon ${getCategoryColor(record.saas_slp_activities?.activity_category)}`}>
                              {getCategoryIcon(record.saas_slp_activities?.activity_category)}
                            </span>
                            <span className="category-name">
                              {record.saas_slp_activities?.activity_category}
                            </span>
                          </div>
                          <span className={`achievement-badge ${getAchievementColor(record.achievement_level)}`}>
                            {record.achievement_level}
                          </span>
                        </div>

                        <div className="record-content">
                          <h5 className="activity-name">
                            {record.saas_slp_activities?.activity_name}
                          </h5>
                          
                          <div className="record-details">
                            <div className="detail-item">
                              <span className="detail-icon">📅</span>
                              <span className="detail-text">
                                {formatDate(record.participation_date)}
                              </span>
                            </div>
                            
                            <div className="detail-item">
                              <span className="detail-icon">📍</span>
                              <span className="detail-text">
                                {record.saas_slp_activities?.location}
                              </span>
                            </div>
                            
                            <div className="detail-item">
                              <span className="detail-icon">🏢</span>
                              <span className="detail-text">
                                {record.saas_slp_activities?.organizer}
                              </span>
                            </div>
                          </div>

                          {record.notes && (
                            <div className="record-notes">
                              <h6>備註:</h6>
                              <p>{record.notes}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {!selectedStudent && (
        <div className="no-student-selected">
          <div className="empty-icon">👥</div>
          <h3>請選擇學生</h3>
          <p>點擊上方學生卡片查看其學習歷程記錄</p>
        </div>
      )}
    </div>
  );
};

export default StudentRecords;
